import { ChangeDetectionStrategy, Component, computed, debounced, effect, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FieldTree, FormField } from '@angular/forms/signals';
import { MatInputModule } from '@angular/material/input';
import { of } from 'rxjs';
import { ComplexPowerDto, ImpdeanceDCDto, ImpedanceAC1PhDto, ImpedanceAC3PhDto, ImpedanceDto, ImpedanceRequestDto, MceApiService, PDto, PowerLossDto, VoltageDropAC1PhDto, VoltageDropAC3PhDto, VoltageDropDCDto, VoltageDropDto } from '../../../core/mceapi.client.generated';
import { appOpt } from '../../../core/mceapi.client.helpers';
import { CalculationBoxComponent } from '../../../shared/components/calculation-box/calculation-box.component';
import { isNone, match, unwrap } from '../../../shared/functions/functional';
import { CableCalculationsStore } from '../cable-calculations.store';
import { EditCable } from '../cable-edit.model';
import { createEarthingSettingsDto } from '../earthing-settings/earthing-settings.component';
import { createExternalCPCDto } from '../external-cpc/external-cpc.component';
import { createInstallationDto } from '../installation/cable-installation.model';
import { createLoadDto } from '../load/load.model';
import { createCableSelectionDto } from '../selection/cable-selection.model';
import { createSystemVoltageDto } from '../system-voltage/system-voltage.model';
import { createEarthingDto } from '../earthing/earthing.model';

// const IMPEDANCE_PARAMS_DEBOUNCE_MS = 300;

function impedanceRequestEqual(
  previous: ImpedanceRequestDto | undefined,
  current: ImpedanceRequestDto | undefined
): boolean {
  return JSON.stringify(previous) === JSON.stringify(current);
}

function formatDCImpedance(impedance: ImpdeanceDCDto): string {
  const resistance = impedance.r1?.value;
  if (resistance === undefined) {
    throw new Error('DC impedance DTO is missing its resistance.');
  }

  return `R: ${resistance.roundTo(6)} Ω`;
}

function formatACImpedance(impedance: ImpedanceAC1PhDto | ImpedanceAC3PhDto): string {
  const z1 = impedance.z1;
  if (z1?.real === undefined || z1.imaginary === undefined || z1.magnitude === undefined) {
    throw new Error('AC impedance DTO is missing required values.');
  }

  return `R: ${z1.real.roundTo(6)}, X: ${z1.imaginary.roundTo(6)} [Z: ${z1.magnitude.roundTo(6)}] Ω`;
}

function formatDCVoltageDrop(voltageDrop: VoltageDropDCDto): string {
  if (voltageDrop.v?.v === undefined || voltageDrop.percent === undefined) {
    throw new Error('DC voltage drop DTO is missing required values.');
  }

  return `Voltage Drop: ${voltageDrop.v.v.roundTo(3)} V [${voltageDrop.percent.roundTo(2)}%]`;
}

function formatAC1PhVoltageDrop(voltageDrop: VoltageDropAC1PhDto): string {
  if (voltageDrop.uo?.uo?.magnitude === undefined || voltageDrop.percent === undefined) {
    throw new Error('AC single-phase voltage drop DTO is missing required values.');
  }

  return `Voltage Drop: ${voltageDrop.uo.uo.magnitude.roundTo(3)} V [${voltageDrop.percent.roundTo(2)}%]`;
}

function formatAC3PhVoltageDrop(voltageDrop: VoltageDropAC3PhDto): string {
  if (voltageDrop.u?.u?.magnitude === undefined || voltageDrop.percent === undefined) {
    throw new Error('AC three-phase voltage drop DTO is missing required values.');
  }

  return `Voltage Drop: ${voltageDrop.u.u.magnitude.roundTo(3)} V [${voltageDrop.percent.roundTo(2)}%]`;
}

function formatDCPowerLoss(powerLoss: PDto): string {
  if (powerLoss.p === undefined) {
    throw new Error('DC power loss DTO is missing its power value.');
  }

  return `Power Loss: ${powerLoss.p.roundTo(0)} W`;
}

function formatACPowerLoss(powerLoss: ComplexPowerDto): string {
  if (powerLoss.s?.real === undefined) {
    throw new Error('AC power loss DTO is missing its real power value.');
  }

  return `Power Loss: ${powerLoss.s.real.roundTo(0)} W`;
}


@Component({
  selector: 'cable-edit-voltage-drop',
  imports: [FormField, MatInputModule, CalculationBoxComponent],
  templateUrl: './cable-voltage-drop.component.html',
})
export class CableVoltageDropComponent {

  readonly form = input.required<FieldTree<EditCable>>();

  private mceApiService = inject(MceApiService);
  readonly store = inject(CableCalculationsStore);

  private readonly impedanceParams = computed<ImpedanceRequestDto | undefined>(() => {
    const systemVoltage = createSystemVoltageDto(this.form().systemVoltage)();
    const cableEarthing = {
      earthing: createEarthingDto(this.form().earthing)(),
      settings: createEarthingSettingsDto(
        this.form().earthingSettings,
        createExternalCPCDto(this.form().externalCPC)()
      )()
    };
    const selectedCable = createCableSelectionDto(this.form().selectedCable)();
    const installation = createInstallationDto(this.form().installation)();
    const conductorTemperature = this.store.conductorTemperature();
    const valid =
      this.form().systemVoltage().valid()
      && this.form().earthing().valid()
      && this.form().earthingSettings().valid()
      && this.form().externalCPC().valid()
      && this.form().selectedCable().valid()
      && this.form().installation().valid();

    if (conductorTemperature === undefined || isNone(selectedCable) || !valid) {
      return undefined;
    }

    return {
      systemVoltage,
      cableEarthing,
      selectedCable: unwrap(selectedCable),
      installation,
      earthing: cableEarthing,
      operatingTemperature: conductorTemperature
    };
  });

  // private readonly debouncedImpedanceParams = debounced(
  //   this.impedanceParams,
  //   IMPEDANCE_PARAMS_DEBOUNCE_MS,
  //   { equal: impedanceRequestEqual }
  // );

  impedanceRx = rxResource({
    params: () => this.impedanceParams(),
    stream: ({ params }) => {
      return this.mceApiService.v1CableImpedance(params);
    }
  });
  impedance = computed<ImpedanceDto | undefined>(() => this.impedanceRx.value());
  _store = effect(() => { this.store.setImpedance(this.impedance());});

  impedanceDC = computed<string>(() => {
    const impedance = this.impedance();
    if (impedance === undefined) return '';
    return match(
      () => '',
      formatDCImpedance)
      (appOpt(impedance.dc ?? {}));
  });

  impedanceAC1phDisplay = computed<string>(() => {
    const impedance = this.impedance();
    if (impedance === undefined) return '';
    return match(
      () => '',
      formatACImpedance)
      (appOpt(impedance.aC1Ph ?? {}));
  });

  impedanceAC3phDisplay = computed<string>(() => {
    const impedance = this.impedance();
    if (impedance === undefined) return '';
    return match(
      () => '',
      formatACImpedance)
      (appOpt(impedance.aC3Ph ?? {}));
  });


  voltageDropRx = rxResource({
    params: () => ({
      systemVoltage: createSystemVoltageDto(this.form().systemVoltage)(),
      load: createLoadDto(this.form().load)(),
      impedance: this.store.impedance(),
      maxVoltageDrop: this.form().voltageDrop.maximumVoltageDrop().value() / 100,
      valid: this.form().systemVoltage().valid() && this.form().load().valid() && this.store.impedance() !== undefined && this.form().voltageDrop.maximumVoltageDrop().valid(),
    }),
    stream: ({ params }) => {

      if (params.impedance === undefined || !params.valid) {
        return of(undefined);
      }

      return this.mceApiService.v1CableVoltageDrop({
        systemVoltage: params.systemVoltage,
        load: params.load,
        cableImpedance: params.impedance,
        maxVoltageDropPoint: params.maxVoltageDrop
      });
    }
  });
  voltageDrop = computed<VoltageDropDto | undefined>(() => this.voltageDropRx.value());
  voltageDropDC = computed<string>(() => {
    const voltageDrop = this.voltageDrop();
    if (voltageDrop === undefined) return '';
    return match(
      () => '',
      formatDCVoltageDrop)
      (appOpt(voltageDrop.dc ?? {}));
  });
  voltageDropAC1ph = computed<string>(() => {
    const voltageDrop = this.voltageDrop();
    if (voltageDrop === undefined) return '';
    return match(
      () => '',
      formatAC1PhVoltageDrop)
      (appOpt(voltageDrop.aC1Ph ?? {}));
  });
  voltageDropAC3ph = computed<string>(() => {
    const voltageDrop = this.voltageDrop();
    if (voltageDrop === undefined) return '';
    return match(
      () => '',
      formatAC3PhVoltageDrop)
      (appOpt(voltageDrop.aC3Ph ?? {}));
  });


  powerLossRx = rxResource({
    params: () => ({
      systemVoltage: createSystemVoltageDto(this.form().systemVoltage)(),
      load: createLoadDto(this.form().load)(),
      impedance: this.store.impedance(),
      valid: this.form().systemVoltage().valid()   && this.form().load().valid()  && this.store.impedance() !== undefined,
    }),
    stream: ({ params }) => {

      if (params.impedance === undefined || !params.valid) {
        return of(undefined);
      }

      return this.mceApiService.v1PowerLoss({
        systemVoltage: params.systemVoltage,
        load: params.load,
        cableImpedance: params.impedance,
      });
    }
  });
  powerLoss = computed<PowerLossDto | undefined>(() => this.powerLossRx.value());
  powerLossDC = computed<string>(() => {
    const powerLoss = this.powerLoss();
    if (powerLoss === undefined) return '';
    return match(
      () => '',
      formatDCPowerLoss)
      (appOpt(powerLoss.dc ?? {}));
  });
  powerLossAC1ph = computed<string>(() => {
    const powerLoss = this.powerLoss();
    if (powerLoss === undefined) return '';
    return match(
      () => '',
      formatACPowerLoss)
      (appOpt(powerLoss.aC1Ph ?? {}));
  });
  powerLossAC3ph = computed<string>(() => {
    const powerLoss = this.powerLoss();
    if (powerLoss === undefined) return '';
    return match(
      () => '',
      formatACPowerLoss)
      (appOpt(powerLoss.aC3Ph ?? {}));
  });

  viewState = computed(() => {
    const impedance = this.impedance();
    const voltageDrop = this.voltageDrop();
    const powerLoss = this.powerLoss();

    return {
      isLoading: this.impedanceRx.isLoading() || this.voltageDropRx.isLoading(),
      status: voltageDrop?.status ?? { case: 'success', message: '' },
      impedance:
        impedance === undefined
        ? 'Enter all required fields to calculate impedance.'
        : impedance.case === 'dc' ? this.impedanceDC()
          : impedance.case === 'ac1ph' ? this.impedanceAC1phDisplay()
            : impedance.case === 'ac3ph' ? this.impedanceAC3phDisplay()
              : 'Unknown impedance case',

      voltageDrop:
        voltageDrop === undefined
        ? 'Enter all required fields to calculate voltage drop.'
          : voltageDrop.case === 'dc' ? this.voltageDropDC()
          : voltageDrop.case === 'ac1ph' ? this.voltageDropAC1ph()
          : voltageDrop.case === 'ac3ph' ? this.voltageDropAC3ph()
          : 'Unknown voltage drop case',

      powerLoss:
        powerLoss === undefined
        ? 'Enter all required fields to calculate power loss.'
        : powerLoss.case === 'dc' ? this.powerLossDC()
          : powerLoss.case === 'ac1ph' ? this.powerLossAC1ph()
            : powerLoss.case === 'ac3ph' ? this.powerLossAC3ph()
              : 'Unknown power loss case'

    };
  });

}
